import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import {
  TrendUpIcon, TrendDownIcon, BankIcon, ReceiptIcon, WalletIcon,
  PackageIcon, CoinsIcon, MoneyIcon, ChartLineUpIcon, ArrowRightIcon,
  ArrowsClockwiseIcon, WarningIcon, BellRingingIcon, ChartPieSliceIcon,
  HandCoinsIcon, BuildingsIcon,
} from "@phosphor-icons/react";

const REFRESH_INTERVAL = 60 * 1000; // 60s

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState({ regiones: [], rtcs: [] });
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({ region: "", rtc: "", desde: "", hasta: "" });
  const [refreshing, setRefreshing] = useState(false);

  const load = async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const params = Object.fromEntries(Object.entries(filtros).filter(([_, v]) => v));
      const [d, m] = await Promise.all([
        api.get("/admin/dashboard", { params }),
        meta.regiones.length === 0 ? api.get("/admin/regiones-rtc") : Promise.resolve({ data: meta }),
      ]);
      setData(d.data);
      setMeta(m.data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); setRefreshing(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filtros]);
  useEffect(() => {
    const t = setInterval(() => load(true), REFRESH_INTERVAL);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [filtros]);

  if (loading) {
    return (
      <Layout title="Panel del Admin General" subtitle="Cargando…">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[1,2,3,4,5,6,7,8].map(i => <div key={i} className="h-28 bg-card border border-border/40 rounded-lg animate-pulse" />)}
        </div>
      </Layout>
    );
  }

  if (!data) return <Layout title="Panel del Admin General"><div className="text-center text-text-muted p-10">Sin datos</div></Layout>;

  const k = data.kpis;

  return (
    <Layout
      title="Panel del Admin General"
      subtitle={`Tablero ejecutivo · ${fmtDate(data.filtros.desde)} → ${fmtDate(data.filtros.hasta)}${refreshing ? " · actualizando…" : ""}`}
      actions={
        <button onClick={() => load(false)} data-testid="admin-refresh" className="px-3 py-1.5 text-xs border border-border rounded-md text-text-secondary hover:border-agro-green hover:text-agro-green flex items-center gap-1.5">
          <ArrowsClockwiseIcon size={14} weight={refreshing ? "fill" : "regular"} className={refreshing ? "animate-spin" : ""} /> Actualizar
        </button>
      }
    >
      {/* Filtros */}
      <Filters filtros={filtros} setFiltros={setFiltros} regiones={meta.regiones} rtcs={meta.rtcs} />

      {/* 10.1 KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <KpiCard
          title="Ventas del período" value={fmtL(k.ventas_mes)} icon={ChartLineUpIcon} accent="green"
          delta={k.ventas_delta_pct} deltaLabel="vs. mes anterior"
          extra={k.meta_mes > 0 ? `Meta: ${fmtL(k.meta_mes)} · ${k.cumplimiento_meta_pct}%` : `Sin meta · Ant.: ${fmtL(k.ventas_mes_anterior)}`}
          onClick={() => navigate("/reportes")}
          testid="kpi-ventas"
        />
        <KpiCard
          title="Año en curso" value={fmtL(k.ventas_anio)} icon={TrendUpIcon} accent="green"
          extra={`${k.facturas_mes} ventas del período${k.ventas_contado_mes ? ` · contado ${fmtL(k.ventas_contado_mes)}` : ""}`}
          onClick={() => navigate("/reportes")}
        />
        <KpiCard
          title="CxC / Vencido" value={fmtL(k.cxc_total)} icon={BankIcon} accent="yellow"
          extra={<span><span className="text-red-400 font-medium">{fmtL(k.cxc_vencido)}</span> vencido</span>}
          onClick={() => navigate("/cxc")}
          testid="kpi-cxc"
        />
        <KpiCard
          title="CxP / Por vencer 7d" value={fmtL(k.cxp_total)} icon={HandCoinsIcon} accent="orange"
          extra={<span><span className="text-agro-yellow">{fmtL(k.cxp_por_vencer_7d)}</span> próx. semana</span>}
          onClick={() => navigate("/cxp")}
          testid="kpi-cxp"
        />
        <KpiCard
          title="Bancos y caja" value={fmtL(k.bancos_total)} icon={WalletIcon} accent="green"
          extra={Object.entries(k.bancos_por_tipo).map(([t, v]) => `${t}: ${fmtL(v)}`).join(" · ")}
          onClick={() => navigate("/tesoreria")}
          testid="kpi-bancos"
        />
        <KpiCard
          title="Utilidad bruta / Margen" value={k.utilidad_bruta == null ? "—" : fmtL(k.utilidad_bruta)} icon={CoinsIcon} accent={k.margen_pct >= 20 ? "green" : k.margen_pct >= 10 ? "yellow" : "red"}
          extra={k.margen_pct == null ? "Solo admin" : `Margen: ${k.margen_pct}% · costo PEPS ${fmtL(k.costo_ventas)}${k.costo_estimado_parcial ? " (parcialmente estimado)" : ""}`}
          testid="kpi-utilidad"
        />
        <KpiCard
          title="Inventario" value={fmtL(k.inventario_valorizado)} icon={PackageIcon} accent="yellow"
          extra={k.productos_bajo_stock > 0 ? `${k.productos_bajo_stock} bajo stock` : "OK"}
          onClick={() => navigate("/productos")}
          testid="kpi-inventario"
        />
        <KpiCard
          title="GRATIVA RTC (devengada)" value={fmtL(k.comisiones_devengadas)} icon={MoneyIcon} accent="yellow"
          extra={`Pendientes: ${fmtL(k.comisiones_pendientes || 0)} · Liquidadas: ${fmtL(k.comisiones_liquidadas ?? k.comisiones_pagadas)}`}
          onClick={() => navigate("/comisiones")}
          testid="kpi-comisiones"
        />
        <KpiCard
          title="Viáticos pendientes" value={k.viaticos_pendientes_n} icon={ReceiptIcon} accent="orange"
          extra={`Monto: ${fmtL(k.viaticos_pendientes_monto)}`}
          onClick={() => navigate("/viaticos")}
          testid="kpi-viaticos"
        />
      </div>

      {/* 10.2 Bancos y liquidez */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        <div className="lg:col-span-2 bg-card border border-border/60 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border/60 flex justify-between items-center">
            <h3 className="font-heading text-base flex items-center gap-2"><WalletIcon size={16} /> Bancos, caja y tarjetas</h3>
            <div className="text-xs text-text-muted">Total: <strong className="text-agro-green">{fmtL(k.bancos_total)}</strong></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 max-h-72 overflow-y-auto">
            {data.bancos.cuentas.map((a) => (
              <div key={a.id} className="bg-app/40 border border-border/40 rounded-md p-3" data-testid={`bank-${a.codigo}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-sm font-medium">{a.nombre}</div>
                    <div className="text-[10px] text-text-muted font-mono">{a.codigo} · {a.tipo}</div>
                  </div>
                  <span className="text-[9px] uppercase bg-border px-1.5 py-0.5 rounded text-text-secondary">{a.moneda}</span>
                </div>
                <div className={`font-heading text-lg mt-2 ${a.saldo > 0 ? "text-agro-green" : a.saldo < 0 ? "text-red-400" : "text-text-muted"}`}>{fmtL(a.saldo)}</div>
              </div>
            ))}
          </div>
          {/* Flujo del período */}
          <div className="px-4 pb-4 pt-2 border-t border-border/40">
            <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">Flujo del período</div>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="bg-agro-green/10 border border-agro-green/30 rounded p-2"><div className="text-[10px] text-text-muted">Entradas</div><div className="text-agro-green font-medium">{fmtL(data.bancos.flujo_periodo.entradas)}</div></div>
              <div className="bg-red-500/10 border border-red-500/30 rounded p-2"><div className="text-[10px] text-text-muted">Salidas</div><div className="text-red-400 font-medium">{fmtL(data.bancos.flujo_periodo.salidas)}</div></div>
              <div className="bg-app border border-border rounded p-2"><div className="text-[10px] text-text-muted">Neto</div><div className={`font-medium ${data.bancos.flujo_periodo.neto >= 0 ? "text-agro-green" : "text-red-400"}`}>{fmtL(data.bancos.flujo_periodo.neto)}</div></div>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border/60">
            <h3 className="font-heading text-base flex items-center gap-2"><HandCoinsIcon size={16} /> Próximos pagos (15d)</h3>
          </div>
          <div className="max-h-96 overflow-y-auto" data-testid="proximos-pagos">
            {data.bancos.proximos_pagos.length === 0 ? (
              <div className="p-6 text-center text-sm text-text-muted">Sin pagos próximos</div>
            ) : data.bancos.proximos_pagos.map((p, i) => (
              <div key={i} className="p-3 border-b border-border/30 text-xs">
                <div className="flex justify-between items-center">
                  <span className="font-mono text-agro-yellow">{p.numero}</span>
                  <span className="font-medium">{fmtL(p.monto)}</span>
                </div>
                <div className="text-text-secondary truncate">{p.proveedor}</div>
                <div className="text-[10px] text-text-muted mt-0.5">Vence: {fmtDate(p.fecha)}</div>
              </div>
            ))}
          </div>
          {data.bancos.obligaciones && (
            <div className="border-t border-border/60 p-3" data-testid="obligaciones-card">
              <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Obligaciones (préstamos)</div>
              <div className="flex justify-between text-xs">
                <span>{data.bancos.obligaciones.prestamos_vigentes} vigentes · saldo</span>
                <span className="font-medium text-orange-400">{fmtL(data.bancos.obligaciones.saldo_total)}</span>
              </div>
              <div className="flex justify-between text-xs mt-0.5">
                <span>Cuotas próximas 30d</span>
                <span className="font-medium">{fmtL(data.bancos.obligaciones.total_proximas)}</span>
              </div>
              {data.bancos.obligaciones.cuotas_vencidas?.count > 0 && (
                <div className="text-[10px] text-red-400 mt-0.5">{data.bancos.obligaciones.cuotas_vencidas.count} cuotas vencidas · {fmtL(data.bancos.obligaciones.cuotas_vencidas.total)}</div>
              )}
              {(data.bancos.obligaciones.proximas_30_dias || []).slice(0, 4).map((c, i) => (
                <div key={i} className="text-[10px] text-text-secondary flex justify-between mt-0.5">
                  <span className="truncate">{c.acreedor} · cuota {c.cuota} · {fmtDate(c.fecha)}</span><span>{fmtL(c.monto)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 10.3 Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <ChartCard title="Ventas por mes (últimos 12)" icon={ChartLineUpIcon}>
          <BarChart data={data.charts.ventas_por_mes} xKey="periodo" yKey="total" />
        </ChartCard>
        <ChartCard title="Cumplimiento de meta (12 meses)" icon={TrendUpIcon}>
          <MetaChart data={data.charts.cumplimiento_meta || []} />
        </ChartCard>
        <ChartCard title="CxC por antigüedad" icon={ChartPieSliceIcon}>
          <BarChart data={data.charts.cxc_aging} xKey="bucket" yKey="monto" colorByValue />
        </ChartCard>
        <ChartCard title="Ventas por región" icon={BuildingsIcon}>
          <HorizontalBar data={data.charts.ventas_por_region} labelKey="region" valueKey="total" />
        </ChartCard>
        <ChartCard title="Ventas por línea / proveedor" icon={PackageIcon}>
          <HorizontalBar data={data.charts.ventas_por_linea} labelKey="linea" valueKey="total" />
        </ChartCard>
        <ChartCard title="Mejores clientes" icon={CoinsIcon}>
          <HorizontalBar data={data.charts.mejores_clientes} labelKey="cliente" valueKey="total" />
        </ChartCard>
        <ChartCard title="Mayores deudores" icon={WarningIcon}>
          <HorizontalBar data={data.charts.mayores_deudores} labelKey="cliente" valueKey="saldo" color="red" />
        </ChartCard>
        <ChartCard title="Inversión por proveedor" icon={BankIcon} span="lg:col-span-2">
          <HorizontalBar data={data.charts.inversion_proveedor} labelKey="proveedor" valueKey="total" color="yellow" />
        </ChartCard>
      </div>

      {/* 10.4 Alertas ejecutivas */}
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border/60 flex items-center gap-2">
          <BellRingingIcon size={18} className="text-agro-yellow" weight="duotone" />
          <div>
            <h3 className="font-heading text-base">Alertas ejecutivas</h3>
            <p className="text-[10px] text-text-muted">Cosas que requieren acción</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 p-4">
          <AlertBox color="red"
            label="Facturas vencidas" big={data.alertas.facturas_vencidas.count}
            sub={fmtL(data.alertas.facturas_vencidas.total)}
            onClick={() => navigate("/cxc")} />
          <AlertBox color="orange"
            label="Sobre límite de crédito" big={data.alertas.clientes_sobre_limite_total ?? data.alertas.clientes_sobre_limite.length}
            sub={data.alertas.clientes_sobre_limite.length > 0 ? `${data.alertas.clientes_sobre_limite[0].cliente}…` : "—"}
            onClick={() => navigate("/clientes")} />
          <AlertBox color={data.alertas.cai_alerta ? "yellow" : "muted"}
            label={`CAI${(data.alertas.cai_alertas || []).length > 1 ? ` (${data.alertas.cai_alertas.length} alertas)` : ""}`}
            big={data.alertas.cai_alerta ? `${data.alertas.cai_alerta.dias_para_vencer}d` : "OK"}
            sub={data.alertas.cai_alerta ? `${data.alertas.cai_alerta.correlativos_restantes ?? "?"} restantes` : "Vigente"}
            onClick={() => navigate("/sar")} />
          <AlertBox color="orange"
            label="Viáticos pendientes" big={data.alertas.viaticos_pendientes.count}
            sub={fmtL(data.alertas.viaticos_pendientes.total)}
            onClick={() => navigate("/viaticos")} />
        </div>

        {data.alertas.clientes_sobre_limite.length > 0 && (
          <div className="border-t border-border/40 p-4">
            <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">Clientes sobre su límite de crédito</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {data.alertas.clientes_sobre_limite.map((c, i) => (
                <div key={i} className="p-2 bg-red-500/5 border border-red-500/30 rounded text-xs">
                  <div className="font-medium">{c.cliente}</div>
                  <div className="text-[10px] text-text-secondary">Saldo: {fmtL(c.saldo)} / Lím: {fmtL(c.limite)} <span className="text-red-400">+{fmtL(c.exceso)}</span></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {data.alertas.inventario_por_vencer.length > 0 && (
          <div className="border-t border-border/40 p-4">
            <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">Inventario próximo a vencer (30d){data.alertas.inventario_vencido_count ? ` · ${data.alertas.inventario_vencido_count} lotes ya vencidos` : ""}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
              {data.alertas.inventario_por_vencer.map((l, i) => (
                <div key={i} className="p-2 bg-agro-yellow/5 border border-agro-yellow/30 rounded">
                  <div className="font-medium">{l.producto_descripcion || l.producto_codigo}</div>
                  <div className="text-[10px] text-text-secondary">Lote {l.lote_codigo || "—"} · {l.es_semilla ? "Test" : "Vence"} {fmtDate(l.fecha_vencimiento)} ({l.dias_restantes}d) · {l.cantidad}u</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

/* ============== COMPONENTES ============== */
function Filters({ filtros, setFiltros, regiones, rtcs }) {
  const clear = () => setFiltros({ region: "", rtc: "", desde: "", hasta: "" });
  const active = filtros.region || filtros.rtc || filtros.desde || filtros.hasta;
  return (
    <div className="bg-card border border-border/60 rounded-lg p-3 mb-5 flex flex-wrap gap-3 items-end">
      <div>
        <label className="text-[9px] uppercase tracking-wider text-text-muted block mb-1">Región</label>
        <select value={filtros.region} onChange={(e) => setFiltros({...filtros, region: e.target.value})} data-testid="filter-region" className="bg-app border border-border rounded-md px-2 py-1.5 text-sm min-w-[140px]">
          <option value="">Todas</option>
          {regiones.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>
      <div>
        <label className="text-[9px] uppercase tracking-wider text-text-muted block mb-1">RTC</label>
        <select value={filtros.rtc} onChange={(e) => setFiltros({...filtros, rtc: e.target.value})} data-testid="filter-rtc" className="bg-app border border-border rounded-md px-2 py-1.5 text-sm min-w-[160px]">
          <option value="">Todos</option>
          {rtcs.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>
      <div>
        <label className="text-[9px] uppercase tracking-wider text-text-muted block mb-1">Desde</label>
        <input type="date" value={filtros.desde} onChange={(e) => setFiltros({...filtros, desde: e.target.value})} className="bg-app border border-border rounded-md px-2 py-1.5 text-sm" />
      </div>
      <div>
        <label className="text-[9px] uppercase tracking-wider text-text-muted block mb-1">Hasta</label>
        <input type="date" value={filtros.hasta} onChange={(e) => setFiltros({...filtros, hasta: e.target.value})} className="bg-app border border-border rounded-md px-2 py-1.5 text-sm" />
      </div>
      {active && <button onClick={clear} className="text-[10px] uppercase tracking-wider text-text-muted hover:text-text-primary px-3 py-1.5 border border-border rounded-md">Limpiar</button>}
      <div className="ml-auto text-[10px] text-text-muted flex items-center gap-1">
        <div className="w-1.5 h-1.5 rounded-full bg-agro-green animate-pulse" /> Tiempo real (auto 60s)
      </div>
    </div>
  );
}

function KpiCard({ title, value, icon: Icon, accent, delta, deltaLabel, extra, onClick, testid }) {
  const c = { green: "text-agro-green", yellow: "text-agro-yellow", orange: "text-orange-400", red: "text-red-400" }[accent] || "text-text-primary";
  return (
    <button onClick={onClick} disabled={!onClick} data-testid={testid}
      className={`text-left bg-card border border-border/60 rounded-lg p-4 transition ${onClick ? "hover:border-agro-green/40 cursor-pointer" : ""}`}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-wider text-text-muted">{title}</div>
        {Icon && <Icon size={16} className={c} weight="duotone" />}
      </div>
      <div className={`font-heading text-2xl mt-1 ${c}`}>{value}</div>
      {delta != null && (
        <div className={`text-[10px] flex items-center gap-1 mt-1 ${delta >= 0 ? "text-agro-green" : "text-red-400"}`}>
          {delta >= 0 ? <TrendUpIcon size={10} weight="bold" /> : <TrendDownIcon size={10} weight="bold" />}
          {delta >= 0 ? "+" : ""}{delta}% {deltaLabel}
        </div>
      )}
      {extra && <div className="text-[10px] text-text-muted mt-1">{extra}</div>}
    </button>
  );
}

function AlertBox({ color, label, big, sub, onClick }) {
  const c = { red: "text-red-400 border-red-500/30 bg-red-500/5", orange: "text-orange-400 border-orange-500/30 bg-orange-500/5", yellow: "text-agro-yellow border-agro-yellow/30 bg-agro-yellow/5", muted: "text-text-muted border-border bg-app/30" }[color];
  return (
    <button onClick={onClick} className={`text-left p-3 rounded border ${c} hover:scale-[1.01] transition`}>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="font-heading text-2xl mt-0.5">{big}</div>
      <div className="text-[10px] opacity-80 mt-0.5">{sub}</div>
    </button>
  );
}

function ChartCard({ title, icon: Icon, children, span = "" }) {
  return (
    <div className={`bg-card border border-border/60 rounded-lg overflow-hidden ${span}`}>
      <div className="p-3 border-b border-border/60 flex items-center gap-2">
        {Icon && <Icon size={14} className="text-agro-green" />}
        <h3 className="font-heading text-sm">{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function BarChart({ data, xKey, yKey, colorByValue }) {
  if (!data || data.length === 0) return <div className="text-center text-text-muted text-xs py-10">Sin datos</div>;
  const max = Math.max(...data.map((d) => d[yKey])) || 1;
  return (
    <div className="space-y-1">
      <div className="flex items-end gap-1 h-32">
        {data.map((d, i) => {
          const h = (d[yKey] / max) * 100;
          const color = colorByValue
            ? (d[xKey] === "Corriente" ? "bg-agro-green" : d[xKey] === "+90" ? "bg-red-500" : d[xKey] === "61–90" ? "bg-orange-500" : "bg-agro-yellow")
            : "bg-agro-green";
          return (
            <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
              <div className={`w-full ${color} rounded-t opacity-80 hover:opacity-100 transition relative group`} style={{ height: `${h}%`, minHeight: "2px" }}>
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-[9px] bg-app border border-border rounded px-1 py-0.5 opacity-0 group-hover:opacity-100 whitespace-nowrap z-10">
                  {fmtL(d[yKey])}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-1 text-[9px] text-text-muted">
        {data.map((d, i) => <div key={i} className="flex-1 text-center truncate">{d[xKey]}</div>)}
      </div>
    </div>
  );
}

function MetaChart({ data }) {
  if (!data || data.length === 0) return <div className="text-center text-text-muted text-xs py-10">Sin datos</div>;
  const max = Math.max(...data.map((d) => Math.max(d.real, d.meta))) || 1;
  const hasMeta = data.some((d) => d.meta > 0);
  return (
    <div className="space-y-1" data-testid="chart-cumplimiento-meta">
      <div className="flex items-end gap-1 h-32">
        {data.map((d, i) => (
          <div key={i} className="flex-1 flex items-end justify-center gap-0.5 h-full group relative"
               title={`${d.periodo}: ${fmtL(d.real)} / meta ${fmtL(d.meta)}${d.cumplimiento_pct != null ? ` (${d.cumplimiento_pct}%)` : ""}`}>
            <div className={`w-1/2 rounded-t ${d.meta > 0 && d.real >= d.meta ? "bg-agro-green" : "bg-agro-yellow"}`} style={{ height: `${(d.real / max) * 100}%`, minHeight: "2px" }} />
            <div className="w-1/3 rounded-t bg-border" style={{ height: `${(d.meta / max) * 100}%`, minHeight: d.meta ? "2px" : 0 }} />
          </div>
        ))}
      </div>
      <div className="flex gap-1 text-[9px] text-text-muted">
        {data.map((d, i) => <div key={i} className="flex-1 text-center truncate">{d.periodo.slice(2)}</div>)}
      </div>
      <div className="text-[9px] text-text-muted">{hasMeta ? "Barra de color = real · gris = meta (verde si cumplió)" : "Sin metas registradas en sales_targets"}</div>
    </div>
  );
}

function HorizontalBar({ data, labelKey, valueKey, color = "green" }) {
  if (!data || data.length === 0) return <div className="text-center text-text-muted text-xs py-6">Sin datos</div>;
  const max = Math.max(...data.map((d) => d[valueKey])) || 1;
  const c = { green: "bg-agro-green", yellow: "bg-agro-yellow", red: "bg-red-500" }[color];
  return (
    <div className="space-y-1.5">
      {data.slice(0, 8).map((d, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <span className="w-32 truncate text-text-secondary">{d[labelKey]}</span>
          <div className="flex-1 bg-app/40 rounded-full h-4 overflow-hidden relative">
            <div className={`${c} h-full rounded-full transition-all`} style={{ width: `${(d[valueKey] / max) * 100}%` }} />
            <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] font-medium text-text-primary">{fmtL(d[valueKey])}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
